import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Sparkles, Users, PawPrint, Gift, Instagram, Facebook } from "lucide-react";
import { PinterestIcon, ThreadsIcon } from "@/components/icons/Social";
import api, { formatApiError } from "@/lib/api";
import { MEDIA } from "@/lib/media";
import { useSeo } from "@/lib/seo";
import ProductCard from "@/components/ProductCard";
import RecentlyViewed from "@/components/RecentlyViewed";
import ReviewsSection from "@/components/ReviewsSection";
import ReviewPhotoStrip from "@/components/ReviewPhotoStrip";
import SeasonalBanner from "@/components/SeasonalBanner";
import PupOfTheMonth from "@/components/PupOfTheMonth";
import PupWinnerSpotlight from "@/components/PupWinnerSpotlight";
import RedCarpetPromo from "@/components/redcarpet/RedCarpetPromo";
import { WaveDivider, Paw, Bone, Star, Blob, PawField } from "@/components/decor";
import { useSocial, handleFromUrl } from "@/lib/social";
import { useCollectionCounts, isLiveLink } from "@/lib/collections";
import { toast } from "sonner";

const COLLECTIONS = [
  { title: "BOOKS", tag: "Adventure starts on every page.", to: "/shop?collection=books", img: MEDIA.collectionBooks, color: "#FFD93D" },
  { title: "CLOTHING", tag: "Wear a little Bulldog happiness.", to: "/shop?collection=unisex", img: MEDIA.collectionClothing, color: "#FF6B9D" },
  { title: "STICKERS & TRANSFERS", tag: "Stick. Press. Smile.", to: "/shop?collection=stickers-transfers", img: MEDIA.collectionStickers, color: "#4CC9F0" },
  { title: "DOG SHOP", tag: "Because Bulldogs deserve merch too.", to: "/shop?collection=dogs", img: MEDIA.collectionDogs, color: "#7ED957" },
  { title: "ACCESSORIES", tag: "A little happiness everywhere you go.", to: "/shop?collection=accessories", img: MEDIA.collectionAccessories, color: "#A78BFA" },
  { title: "GIFTS", tag: "For Bulldog people and their humans.", to: "/shop?collection=gifts", img: MEDIA.collectionGifts, color: "#FF6B9D" },
];

const VALUES = [
  { icon: Sparkles, color: "#FFD93D", title: "MADE TO MAKE YOU SMILE", text: "Happy products inspired by two very real Bulldogs." },
  { icon: Heart, color: "#FF6B9D", title: "CREATED WITH LOVE", text: "A small Australian brand built around family, storytelling and our dogs." },
  { icon: Users, color: "#4CC9F0", title: "FOR BULLDOG PEOPLE", text: "Made for people who understand that Bulldogs aren't pets — they're personalities." },
  { icon: PawPrint, color: "#7ED957", title: "ADVENTURES FOR EVERYONE", text: "Books, clothing and goodies designed for children, grown-ups and four-legged family members." },
];

function SectionHeading({ kicker, title, subtitle, center }) {
  return (
    <div className={`${center ? "text-center mx-auto" : ""} max-w-2xl mb-8 sm:mb-10`}>
      {kicker && <span className="font-hand text-2xl text-[#FF6B9D]">{kicker}</span>}
      <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#19324A] leading-tight">{title}</h2>
      {subtitle && <p className="mt-3 text-[#19324A]/70 text-base sm:text-lg">{subtitle}</p>}
    </div>
  );
}

function NewsletterForm() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Please tick the box to join the pack."); return; }
    setLoading(true);
    try {
      await api.post("/newsletter", { first_name: firstName, email, consent });
      toast.success("Woohoo! Welcome to the pack 🐾");
      setFirstName(""); setEmail(""); setConsent(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="w-full max-w-xl mx-auto" data-testid="newsletter-form">
      <div className="flex flex-col sm:flex-row gap-3">
        <input value={firstName} onChange={(e) => setFirstName(e.target.value)} required placeholder="First Name"
          className="flex-1 rounded-full px-5 py-3.5 border-2 border-transparent focus:border-[#FFD93D] outline-none text-[#19324A]" data-testid="newsletter-firstname" />
        <input value={email} onChange={(e) => setEmail(e.target.value)} required type="email" placeholder="Email Address"
          className="flex-1 rounded-full px-5 py-3.5 border-2 border-transparent focus:border-[#FFD93D] outline-none text-[#19324A]" data-testid="newsletter-email" />
      </div>
      <label className="flex items-start gap-2 mt-4 text-sm text-white cursor-pointer" data-testid="newsletter-consent-label">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-[#FF6B9D] w-4 h-4" data-testid="newsletter-consent" />
        <span>Yes, I'd like to join The Happy Bulldog Pack and receive news, launches and offers. I can unsubscribe anytime.</span>
      </label>
      <button type="submit" disabled={loading} className="btn-primary w-full sm:w-auto mt-4" data-testid="newsletter-submit">
        {loading ? "Joining…" : "JOIN THE PACK"}
      </button>
    </form>
  );
}

export default function Home() {
  useSeo("Home of Chewie & Solo — British Bulldog Books, Clothing & Gifts",
    "Welcome to The Happy Bulldog Co.™ — the colourful home of Chewie & Solo. Shop British Bulldog children's books, clothing, accessories, dog merch and gifts in Australia.");
  const [products, setProducts] = useState([]);
  const social = useSocial();
  const counts = useCollectionCounts();
  const liveCollections = COLLECTIONS.filter((c) => isLiveLink(c.to, counts));
  const [seasonal, setSeasonal] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);

  useEffect(() => {
    api.get("/products?limit=100").then(({ data }) => setProducts(data.products)).catch(() => {});
    api.get("/seasonal-collections").then(({ data }) => setSeasonal(data.collections)).catch(() => {});
    api.get("/products/best-sellers?limit=4").then(({ data }) => setBestSellers(data.products)).catch(() => {});
  }, []);

  // Auto-feature the newest published products (flagged is_new first, then by date)
  const newArrivals = [...products]
    .sort((a, b) => (Number(!!b.is_new) - Number(!!a.is_new)) || (b.created_at || "").localeCompare(a.created_at || ""))
    .slice(0, 8);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#EAF7FF] via-[#FFF9EE] to-[#FFF9EE]" data-testid="hero-section">
        <PawField color="#4CC9F0" opacity={0.06} />
        <Blob className="absolute -top-16 -left-16 w-72 h-72 text-[#FFD93D]/40" />
        <Blob className="absolute top-24 right-0 w-56 h-56 text-[#FF6B9D]/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-8 lg:pt-14 pb-6 grid lg:grid-cols-2 gap-8 lg:gap-10 items-center">
          <div className="hb-fade-up order-2 lg:order-1 text-center lg:text-left">
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              <span className="text-[#19324A]">Big Personalities.</span><br />
              <span className="text-[#FF6B9D]">Little Paws.</span><br />
              <span className="text-[#4CC9F0]">Endless Adventures.</span>
            </h1>
            <p className="mt-5 text-[#19324A]/80 text-base sm:text-lg max-w-lg mx-auto lg:mx-0 font-medium">
              Welcome to The Happy Bulldog Co.™ — the colourful home of Chewie & Solo, two lovable British Bulldogs whose cheeky adventures have leapt from the pages of their books into clothing, gifts, accessories and plenty of bulldog mischief.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <Link to="/shop" className="btn-pink" data-testid="hero-shop-btn">SHOP THE COLLECTION <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/meet" className="btn-outline bg-white/80 backdrop-blur" data-testid="hero-meet-btn">DISCOVER CHEWIE & SOLO</Link>
            </div>
            <div className="mt-9 flex items-center gap-3 sm:gap-5 flex-wrap justify-center lg:justify-start">
              {[["Books", "/shop?collection=books", "#FF6B9D"], ["Clothing", "/shop?collection=unisex", "#4CC9F0"], ["Accessories", "/shop?collection=accessories", "#FFD93D"], ["Dog Shop", "/shop?collection=dogs", "#7ED957"]].filter(([, to]) => isLiveLink(to, counts)).map(([label, to, color], i) => (
                <React.Fragment key={label}>
                  {i > 0 && <span className="h-8 w-px bg-[#19324A]/15" />}
                  <Link to={to} className="flex flex-col items-center gap-1 group" data-testid={`hero-cat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <Paw className="w-7 h-7 transition-transform group-hover:-translate-y-1" style={{ color }} />
                    <span className="font-display font-semibold text-sm text-[#19324A]">{label}</span>
                  </Link>
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="order-1 lg:order-2 hb-fade-up">
            <div className="rounded-[2rem] overflow-hidden soft-shadow ring-4 ring-white bg-white">
              <img src={MEDIA.heroDogs} alt="Chewie and Solo, the two real British Bulldogs behind The Happy Bulldog Co." className="w-full object-cover" />
            </div>
          </div>
        </div>
        <WaveDivider color="#FFF9EE" />
      </section>

      {/* CROWNED PUP OF THE MONTH — announcement right under the hero */}
      <PupWinnerSpotlight />
      <RedCarpetPromo />

      {/* SEASONAL HERO BANNER (auto-swaps with the active occasion) */}
      <SeasonalBanner />

      {/* SHOP BY COLLECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="collections-section">
        <SectionHeading kicker="Have a sniff around" title="Shop by Collection" center />
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 ${liveCollections.length === 4 ? "lg:grid-cols-2 max-w-4xl mx-auto" : "lg:grid-cols-3"}`}>
          {liveCollections.map((c) => (
            <Link key={c.title} to={c.to} className="group relative rounded-3xl overflow-hidden soft-shadow card-hover aspect-[4/3]" data-testid={`collection-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <img src={c.img} alt={c.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${c.color}ee 0%, ${c.color}66 40%, transparent 75%)` }} />
              <div className="absolute inset-0 p-5 flex flex-col justify-end text-left">
                <h3 className="font-display font-extrabold text-2xl text-white drop-shadow">{c.title}</h3>
                <p className="text-white/95 font-medium text-sm mt-1">{c.tag}</p>
                <span className="mt-2 inline-flex items-center gap-1 text-white font-display font-semibold text-sm">Shop now <ArrowRight className="w-4 h-4" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BUNDLE BUILDER CTA — hidden automatically when any bundle slot has no live products */}
      {isLiveLink("/bundle-builder", counts) && <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-2" data-testid="bundle-cta">
        <Link to="/bundle-builder" className="block rounded-3xl overflow-hidden soft-shadow card-hover bg-gradient-to-r from-[#A78BFA] to-[#FF6B9D] text-white p-7 sm:p-10 relative">
          <PawField color="#ffffff" opacity={0.12} />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <span className="font-hand text-2xl text-[#FFD93D]">New!</span>
              <h3 className="font-display font-extrabold text-2xl sm:text-3xl">🎁 Build-Your-Own Bundle</h3>
              <p className="text-white/90 mt-1">Mix a book, a tee and a bandana into your own gift set — one happy set price.</p>
            </div>
            <span className="btn-primary shrink-0">Build a Bundle <ArrowRight className="w-4 h-4" /></span>
          </div>
        </Link>
      </section>}

      {/* SEASONAL STRIP */}
      {seasonal.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-4" data-testid="seasonal-strip">
          <div className="rounded-3xl bg-gradient-to-r from-[#A78BFA]/20 via-[#FF6B9D]/15 to-[#FFD93D]/25 p-6 sm:p-8 soft-shadow">
            <div className="flex items-center gap-2 mb-4"><Star className="w-5 h-5 text-[#A78BFA]" /><span className="font-hand text-2xl text-[#A78BFA]">Special right now</span></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {seasonal.map((s) => (
                <Link key={s.id} to={`/shop?collection=${s.slug}`} className="bg-white rounded-2xl p-5 soft-shadow card-hover flex items-center justify-between gap-3" data-testid={`seasonal-card-${s.slug}`}>
                  <div><h3 className="font-display font-bold text-lg text-[#19324A]">{s.label}</h3><p className="text-sm text-[#19324A]/60">{s.description}</p></div>
                  <ArrowRight className="w-5 h-5 text-[#FF6B9D] shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MEET THE REAL STARS */}
      <section className="relative bg-[#7ED957]/15 py-14 sm:py-20 overflow-hidden" data-testid="meet-stars-section">
        <WaveDivider color="#7ED957" flip className="opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div className="relative">
            <div className="rounded-[2.5rem] overflow-hidden soft-shadow ring-4 ring-white">
              <img src={MEDIA.heroDogs} alt="Chewie and Solo relaxing together" className="w-full object-cover" />
            </div>
            <Bone className="absolute -top-5 -right-3 w-24 text-[#FFD93D] hb-float hidden sm:block" />
          </div>
          <div>
            <SectionHeading kicker="The real stars" title="Meet the Real Stars" />
            <div className="space-y-4 text-[#19324A]/80 text-base sm:text-lg">
              <p>They snore. They steal the best seat on the couch. They have very strong opinions about biscuits.</p>
              <p>Meet <strong>Chewie & Solo</strong> — the real British Bulldogs behind The Happy Bulldog Co.™ and the stars of the Chewie & Solo Adventure books. What began with their everyday mischief has grown into stories, characters and a happy little world of products inspired by the two Bulldogs who started it all.</p>
            </div>
            <Link to="/meet" className="btn-pink mt-6" data-testid="meet-stars-btn">MEET CHEWIE & SOLO <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* FEATURED BOOK */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20" data-testid="featured-book-section">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="relative order-2 lg:order-1">
            <Blob className="absolute -inset-6 w-full h-full text-[#FFD93D]/30 scale-110" />
            <img src={MEDIA.bookCover} alt="Chewie & Solo: The Great Biscuit Bonanza book cover" className="relative rounded-3xl soft-shadow w-full max-w-md mx-auto" />
          </div>
          <div className="order-1 lg:order-2">
            <span className="font-hand text-2xl text-[#4CC9F0]">The featured adventure</span>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#19324A] leading-tight mt-1">CHEWIE & SOLO<br /><span className="text-[#FF6B9D]">The Great Biscuit Bonanza</span></h2>
            <div className="mt-5 space-y-3 text-[#19324A]/80 text-base sm:text-lg">
              <p>What happens when two cheeky Bulldogs, a suspicious number of biscuits and one very determined robot vacuum collide?</p>
              <p>A wonderfully messy Chewie & Solo adventure begins.</p>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <span className="bg-white soft-shadow rounded-full px-4 py-1.5 text-sm font-semibold text-[#19324A]">Ages 3–8</span>
              <span className="bg-white soft-shadow rounded-full px-4 py-1.5 text-sm font-semibold text-[#19324A]">Paperback · 32 pages</span>
              <span className="bg-white soft-shadow rounded-full px-4 py-1.5 text-sm font-semibold text-[#19324A]">Read-aloud favourite</span>
            </div>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/product/chewie-solo-the-great-biscuit-bonanza" className="btn-primary" data-testid="book-discover-btn">DISCOVER THE BOOK</Link>
              <Link to="/product/chewie-solo-the-great-biscuit-bonanza" className="btn-outline" data-testid="book-buy-btn">BUY NOW</Link>
            </div>
          </div>
        </div>
      </section>

      {/* BEST SELLERS */}
      <section className="relative bg-[#FFD93D]/20 py-14 sm:py-20 overflow-hidden" data-testid="best-sellers-section">
        <img src={MEDIA.soloCutout} alt="" aria-hidden="true" className="hidden lg:block absolute bottom-0 left-0 w-32 xl:w-40 hb-float pointer-events-none select-none" style={{ animationDelay: "0.4s" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <SectionHeading kicker="The goodies everyone is sniffing out" title="The Pack's Favourites 🐾" center />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {bestSellers.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
          <div className="text-center mt-10">
            <Link to="/shop?best_seller=true" className="btn-outline" data-testid="best-sellers-all-btn">See all best sellers <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      <ReviewPhotoStrip />
      <ReviewsSection />

      {/* BULLDOG PERSONALITY */}
      <section className="relative bg-[#FF6B9D] text-white py-16 sm:py-24 overflow-hidden" data-testid="personality-section">
        <PawField color="#ffffff" opacity={0.12} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-display font-extrabold text-4xl sm:text-6xl lg:text-7xl leading-[0.95] drop-shadow-sm">
            SERIOUSLY CUTE.<br />SLIGHTLY NAUGHTY.<br /><span className="text-[#FFD93D]">COMPLETELY BULLDOG.</span>
          </h2>
          <p className="mt-6 text-white/95 text-lg sm:text-xl font-medium">Life is better with a little Bulldog personality.</p>
          <Link to="/shop" className="btn-primary mt-8" data-testid="personality-shop-btn">SHOP HAPPY <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20" data-testid="new-arrivals-section">
        <SectionHeading kicker="Fresh from the dog house" title="New Chewie & Solo Goodies" subtitle="New Chewie & Solo goodies have arrived." />
        <div className="flex gap-4 sm:gap-6 overflow-x-auto no-scrollbar snap-x pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-3 lg:grid-cols-4">
          {newArrivals.map((p) => (
            <div key={p.id} className="min-w-[70%] sm:min-w-0 snap-start"><ProductCard product={p} /></div>
          ))}
        </div>
      </section>

      {/* RECENTLY VIEWED */}
      <RecentlyViewed className="pb-4 sm:pb-6" />

      {/* BRAND VALUES */}
      <section className="bg-[#4CC9F0]/15 py-14 sm:py-20" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {VALUES.map((v) => (
            <div key={v.title} className="bg-white rounded-3xl p-6 soft-shadow text-center card-hover">
              <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center" style={{ background: `${v.color}33` }}>
                <v.icon className="w-8 h-8" style={{ color: v.color === "#FFD93D" ? "#E0B400" : v.color }} />
              </div>
              <h3 className="font-display font-bold text-lg text-[#19324A] mt-4">{v.title}</h3>
              <p className="text-[#19324A]/70 text-sm mt-2">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="relative bg-[#19324A] text-white py-16 sm:py-24 overflow-hidden" data-testid="newsletter-section">
        <Blob className="absolute -top-10 -right-10 w-64 h-64 text-[#FF6B9D]/20" />
        <Blob className="absolute bottom-0 -left-16 w-72 h-72 text-[#4CC9F0]/20" />
        <img src={MEDIA.chewieCutout} alt="" aria-hidden="true" className="hidden xl:block absolute bottom-0 right-6 w-40 hb-float pointer-events-none select-none" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <span className="font-hand text-2xl text-[#FFD93D]">The Happy Bulldog Pack</span>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl mt-1 text-white">Join the Pack 🐾</h2>
          <p className="mt-4 text-white/80 text-base sm:text-lg">Be the first to hear about new Chewie & Solo adventures, book releases, new merchandise, special offers and whatever mischief the Bulldogs get into next.</p>
          <div className="mt-8"><NewsletterForm /></div>
        </div>
      </section>

      {/* SOCIAL */}
      <PupOfTheMonth />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center" data-testid="social-section">
        <SectionHeading kicker="Follow the mischief" title="Follow the Mischief" subtitle="Chewie & Solo have been up to something again…" center />
        <div className="grid grid-cols-3 gap-3 sm:gap-4" data-testid="social-tiles">
          {[MEDIA.heroDogs, MEDIA.chewie, MEDIA.solo].map((img, i) => (
            <div key={i} className="relative rounded-2xl overflow-hidden aspect-square soft-shadow group" data-testid={`social-tile-${i}`}>
              <img src={img} alt="Chewie and Solo on social media" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-[#19324A]/0 group-hover:bg-[#19324A]/30 transition-colors flex items-center justify-center">
                <Facebook className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-center gap-3 mt-8 flex-wrap">
          {social.instagram && (
            <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="btn-pink !px-6" data-testid="follow-instagram"><Instagram className="w-5 h-5" /> {handleFromUrl(social.instagram)}</a>
          )}
          {social.facebook ? (
            <a href={social.facebook} target="_blank" rel="noopener noreferrer" className={`${social.instagram ? "btn-outline" : "btn-pink"} !px-6`} data-testid="follow-facebook"><Facebook className="w-5 h-5" /> Follow us on Facebook</a>
          ) : (
            <span className="btn-outline !px-6 opacity-60 cursor-default" data-testid="follow-facebook-soon"><Facebook className="w-5 h-5" /> Facebook — coming soon</span>
          )}
          {social.pinterest && (
            <a href={social.pinterest} target="_blank" rel="noopener noreferrer" className="btn-outline !px-6" data-testid="follow-pinterest"><PinterestIcon className="w-5 h-5" /> Pin with us on Pinterest</a>
          )}
          {social.threads && (
            <a href={social.threads} target="_blank" rel="noopener noreferrer" className="btn-outline !px-6" data-testid="follow-threads"><ThreadsIcon className="w-5 h-5" /> {handleFromUrl(social.threads)} on Threads</a>
          )}
        </div>
      </section>
    </div>
  );
}
